import { ArrowRight, BookOpen, Building2, CircleDot, Clock3, FileSpreadsheet, FolderOpen, Map, Plus, Presentation, Search, Settings2, Trash2, Upload } from 'lucide-react'
import { formatDate, phaseProgress, processPhases, referenceUpdateAvailable, stepTemporalMetrics, type StepTemporalMetrics } from '../lib/project'
import type { GeneralReferenceRow, ProjectRow } from '../types'
import { HelpTooltip } from '../components/HelpTooltip'

type PortfolioViewProps = {
  projects: ProjectRow[]
  onOpen: (project: ProjectRow) => void
  onCreate: () => void
  onOpenReference: (view: 'overview' | 'plan-2d' | 'presentation' | 'editor') => void
  reference?: GeneralReferenceRow | null
  userLabel?: string
  onOpenTrash?: () => void
  onRestoreArchive?: (file: File) => void
  busy?: boolean
}

export function PortfolioView({ projects, onOpen, onCreate, onOpenReference, reference, userLabel = 'Alassane', onOpenTrash = () => {}, onRestoreArchive = () => {}, busy = false }: PortfolioViewProps) {
  return (
    <main className="workspace portfolio-view">
      <div className="page-heading">
        <div>
          <span className="eyebrow">PORTEFEUILLE PROJETS</span>
          <h1>Bonjour {userLabel.split(' ')[0] || userLabel},</h1>
          <p>Voici la situation de vos projets. Ouvrez celui qui demande votre attention.</p>
        </div><div className="portfolio-heading-actions">
          <button className="secondary-button" onClick={onOpenTrash} type="button"><Trash2 size={17} /> Corbeille</button>
          <label className={`secondary-button archive-upload-button ${busy ? 'disabled' : ''}`} title="Restaurer une copie de travail depuis une archive Suivi Aziz">
            <Upload size={17} /> Restaurer une archive
            <input disabled={busy} type="file" accept=".zip,application/zip,application/x-zip-compressed" onChange={(event) => { const file = event.target.files?.[0]; if (file) onRestoreArchive(file); event.currentTarget.value = '' }} />
          </label>
          <button className="primary-button" disabled={busy} onClick={onCreate} type="button"><Plus size={17} /> Nouveau projet</button>
        </div>
      </div>

      <section className="portfolio-summary" aria-label="Résumé du portefeuille">
        <div><Building2 /><strong>{projects.length}</strong><span>projets</span></div>
        <div><CircleDot /><strong>{projects.filter((project) => project.snapshot.project.current_phase_id !== 'PH-05').length}</strong><span>parcours actifs <HelpTooltip text="Projets dont le parcours administratif n’est pas encore arrivé à la dernière phase." label="Que signifie parcours actif ?" align="left" /></span></div>
        <div><Clock3 /><strong>{projects.filter((project) => project.snapshot.project.step_states.some((step) => step.status === 'En attente' || step.status === 'Bloqué')).length}</strong><span>à surveiller <HelpTooltip text="Projets contenant au moins une étape en attente ou bloquée." label="Pourquoi ces projets sont-ils à surveiller ?" align="right" /></span></div>
      </section>

      <section className="home-reference-card">
        <div className="home-reference-copy"><span className="reference-home-icon"><BookOpen size={24} /></span><div><span className="eyebrow">RÉFÉRENCE GÉNÉRALE</span><h2>Parcours administratif de référence</h2><p>Consultez ou préparez le parcours général, en dehors de tout projet.</p></div></div>
        <div className="home-reference-actions">
          <button onClick={() => onOpenReference('overview')} type="button"><BookOpen size={17} /> Ouvrir</button>
          <button onClick={() => onOpenReference('plan-2d')} type="button"><Map size={17} /> Parcours visuel</button>
          <button onClick={() => onOpenReference('presentation')} type="button"><Presentation size={17} /> Présentation</button>
          <button onClick={() => onOpenReference('editor')} type="button"><Settings2 size={17} /> Nouvelle version</button>
        </div>
      </section>

      <section className="project-ledger">
        <header className="ledger-toolbar">
          <div>
            <h2>Projets</h2>
            <span>Situation de travail</span>
          </div>
          <label className="search-field">
            <Search size={17} />
            <input type="search" placeholder="Rechercher un projet" aria-label="Rechercher un projet" />
          </label>
        </header>

        <div className="ledger-head" aria-hidden="true">
          <span>Projet</span><span>Situation</span><span>Parcours</span><span>Mise à jour</span><span />
        </div>

        {projects.map((project) => {
          const hasReferenceUpdate = Boolean(reference && referenceUpdateAvailable(project.snapshot, reference.content))
          const currentPhase = processPhases(project.snapshot).find((phase) => phase.id === project.current_phase_id)
          const progress = phaseProgress(project.snapshot, project.current_phase_id)
          const currentState = project.snapshot.project.step_states.find((step) => step.step_id === project.current_step_id)
          const waiting = currentState?.waiting_days
          const temporal: StepTemporalMetrics = currentState ? stepTemporalMetrics(currentState, project.as_of_date) : {}
          const temporalLine = temporal.delayDays !== undefined
            ? `Dépassement indicatif de ${temporal.delayDays} jour${temporal.delayDays > 1 ? 's' : ''}`
            : temporal.remainingDays !== undefined
              ? `${temporal.remainingDays} jour${temporal.remainingDays > 1 ? 's' : ''} indicatif${temporal.remainingDays > 1 ? 's' : ''} restant${temporal.remainingDays > 1 ? 's' : ''}`
              : temporal.deadlineDate
                ? `Fin prévue le ${formatDate(temporal.deadlineDate)}`
                : waiting !== undefined
                  ? `En attente depuis ${waiting} jours`
                  : project.scenario
          return (
            <button className="ledger-row" onClick={() => onOpen(project)} key={project.id} type="button">
              <span className="project-identity">
                <span className="project-monogram"><Building2 size={22} /></span>
                <span><strong>{project.name}</strong><small>{project.code} · {project.city}</small>{project.snapshot.dataset_kind === 'prototype_fictif' ? <span className="project-kind-badge">Projet fictif</span> : null}{hasReferenceUpdate ? <span className="reference-update-badge">Référence à vérifier</span> : null}</span>
              </span>
              <span className="project-situation">
                <strong>{project.status_label}</strong>
                <small>{temporalLine}</small>
              </span>
              <span className="project-progress">
                <span><strong>Phase {currentPhase?.order}</strong><small>{currentPhase?.name}</small></span>
                <span className="mini-progress"><i style={{ width: `${progress.percent}%` }} /></span>
              </span>
              <span className="project-date">{formatDate(project.as_of_date)}</span>
              <span className="row-action">Ouvrir <ArrowRight size={17} /></span>
            </button>
          )
        })}
      </section>

      <aside className="familiar-bridge">
        <div className="bridge-icon"><FileSpreadsheet size={24} /></div>
        <div><strong>Vous préférez travailler dans Excel ? <HelpTooltip text="Le fichier reste utilisable dans Excel. À son retour, Suivi Aziz vous montre les différences sans rien écraser automatiquement." label="Comment fonctionne le pont Excel ?" align="left" /></strong><span>Ouvrez un projet, exportez sa fiche puis soumettez le fichier actualisé. Vous validerez chaque différence.</span></div>
        <FolderOpen size={21} />
      </aside>
    </main>
  )
}
